import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { finalize, Subject } from 'rxjs';

import { PDF_REPOSITORY, PdfRepository } from '@app/domain/repositories/ptrab/pdf/pdf.repository.contract';
import { TypeIncome } from '@app/entities/enums/ptrab/documents/income.enum';
import { Income } from '@app/entities/interfaces/ptrab/income.interface';
import { ErrorCodes, ErrorMessages } from '@app/entities/models/error/error.model';
import { Logger, AlertService } from '@app/services';
import { LoadingService } from '@app/services/loading/loading.service';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';
import { decodeResponse } from '@app/shared/utils/utils';

import { MTranslateService } from '@mercadona/core/translate';

import { PdfUseCases } from './pdf.use-cases.contract';

@Injectable()
export class PdfUseCasesImpl implements PdfUseCases {
  #errorSubject: Subject<ErrorCodes> = new Subject<ErrorCodes>();
  error$ = this.#errorSubject.asObservable();

  private logger = new Logger('PdfService');
  private readonly url = `/ptrab/payslip`;

  #pdfRepository: PdfRepository = inject(PDF_REPOSITORY);
  loadingService: LoadingService = inject(LoadingService);
  translateService: MTranslateService = inject(MTranslateService);
  alertService: AlertService = inject(AlertService);

  constructor(private datePipe: DatePipe) {}

  fetch(url: string, errorMsg?: string, isExternal = false, params = {}, isPtUser = false) {
    this.loadingService.show('COMMON.DOWNLOADING');

    if (isExternal) {
      this.openExternal(url);
      return;
    }

    this.#pdfRepository
      .fetch(url, params)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe({
        next: (response) => this.open(response),
        error: (err) => this.handleError(errorMsg, err, isPtUser)
      });
  }

  fetchDirectDebit(url: string) {
    this.loadingService.show('COMMON.DOWNLOADING');

    this.#pdfRepository
      .fetchDirectDebitPdf(url)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe({
        next: (response) => this.open(response),
        error: (err) => this.handleError('', err)
      });
  }

  fetchPayslipPdf(payslipId: string, isEmployee: boolean) {
    try {
      const url = `${this.url}/${payslipId}/duplicate` + (isEmployee ? '' : '/exemployee');
      this.fetch(url, 'EMPLOYEE_PORTAL.ERROR_DOWNLOAD');
    } catch (err) {
      this.logger.error(err);
      if (decodeResponse(err).status === ErrorCodes.UNAUTHORIZED) {
        return;
      }

      this.alertService.showError(
        this.translateService.translate('COMMON.ERROR'),
        this.translateService.translate('UNEXPECTED.ERROR.TITLE')
      );
    }
  }

  fetchMOTVacationsPdf() {
    try {
      let params = new HttpParams();
      params = params.set('lang', this.translateService.getActiveLang());
      const url = `/mot/v2/vacations/pdf`;
      this.fetch(url, 'MOT.VACATIONS.ERRORS.DOWNLOAD_PDF', false, params);
    } catch (err) {
      this.logger.error(err);
    }
  }

  fetchWithholdingPdf(year: number, isEmployee: boolean, companyCode: string, certificateType: string) {
    try {
      let params = new HttpParams();
      params = params.set('year', year);

      if (companyCode) {
        params = params.set('company_code', companyCode);
      }
      if (certificateType) {
        params = params.set('certificate_type', certificateType);
      }

      const url = this.url + (isEmployee ? `/withholding/certificate` : '/withholding/certificate/exemployee');
      this.fetch(url, 'WITHHOLDING.CERTIFICATE.EMPTY', false, params);
    } catch (err) {
      this.logger.error(err);
    }
  }

  fetchContributionsCertificate() {
    try {
      const url = `/ptrab/certificate/disability/exemployee`;
      this.fetch(url, 'WITHHOLDING.CERTIFICATE.EMPTY', false, {});
    } catch (err) {
      this.logger.error(err);
    }
  }

  fetchLogisticCalendarPdf(year: string) {
    try {
      let params = new HttpParams();
      params = params.set('lang', this.translateService.getActiveLang());
      params = params.set('year', year);
      const url = `/organiza2/v1/pdf`;
      this.fetch(url, 'MOT.VACATIONS.ERRORS.DOWNLOAD_PDF', false, params);
    } catch (err) {
      this.logger.error(err);
    }
  }

  fetchRiskyPregnancy(companyCode: string, employeeNumber: string, startDate: string) {
    try {
      let params = new HttpParams();
      params = params.set('company_code', companyCode);
      params = params.set('employee_number', employeeNumber);
      params = params.set('start_date', startDate);
      const url = `/ptrab/certificate/risk-during-pregnancy`;
      this.fetch(url, '', false, params);
    } catch (err) {
      this.logger.error(err);
    }
  }

  fetchCarTransferPdf(carTransferId: string) {
    try {
      const url = `${this.url}/car-transfer/${carTransferId}/copy`;
      this.fetch(url, 'CAR_TRANSFER.ERROR.DOWNLOADING');
    } catch (err) {
      this.logger.error(err);
    }
  }

  fetchSignedTimetablePdf(documentId: string) {
    try {
      const url = `/mot/v2/signed-timetables/${documentId}`;
      this.fetch(url);
    } catch (err) {
      this.logger.error(err);
    }
  }

  fetchSignedVacationsPdf(documentId: string) {
    try {
      const url = `/mot/v3/signed-vacations/${documentId}`;
      this.fetch(url);
    } catch (err) {
      this.logger.error(err);
    }
  }

  fetchVacationsYearPdf(year: number) {
    try {
      const url = `/timemanage/v1/calendar/${year}/pdf`;
      this.fetch(url);
    } catch (err) {
      this.logger.error(err);
    }
  }

  fetchIncomeAndAssetsPdf(income: Income) {
    const params: Params = {
      certificate_type: income.revenue
    };

    if (income.revenue === TypeIncome.YEARLY) {
      if (income.yearType === TypeIncome.PREVIOUS_ANNUAL) {
        params.certificate_type = TypeIncome.PREVIOUS_ANNUAL;
        params.year = income.year;
      } else if (income.yearType === TypeIncome.YEARLY_FORECAST) {
        params.certificate_type = TypeIncome.YEARLY_FORECAST;
      }
    } else if (income.revenue === TypeIncome.YEARLY_RANGE) {
      const fromDate = new Date();
      fromDate.setFullYear(income.fromMonth.year, income.fromMonth.month, 1);
      params.from_date = this.datePipe.transform(fromDate, 'yyyy-MM-dd');

      const toDate = new Date();
      toDate.setFullYear(income.untilMonth.year, income.untilMonth.month, 1);
      params.to_date = this.datePipe.transform(toDate, 'yyyy-MM-dd');
    }

    try {
      const url = `/ptrab/certificate/income-and-assets`;
      this.fetch(url, '', undefined, params);
    } catch (err) {
      this.logger.error(err);
    }
  }

  handleError(errorMsg: string | undefined, err: MSafeAny, isPtUser?: boolean) {
    const decodedResponse = decodeResponse(err);
    this.#errorSubject.next(decodedResponse.status);
    this.logger.error(decodedResponse);
    const codeExceptions = [ErrorCodes.UNAUTHORIZED, ErrorCodes.BAD_REQUEST];

    // TODO: Temporary fix. Generic error management refactor is pending.
    if (decodedResponse?.error?.code === ErrorMessages.FIXED_DISCONTINUOUS_CERT_DOWNLOAD) {
      this.alertService.showError(
        this.translateService.translate('COMMON.DISPLAY_NOT_AVAILABLE'),
        decodedResponse.error.message
      );

      return;
    }

    if (codeExceptions.indexOf(decodedResponse.status) >= 0) {
      throw decodedResponse;
    }

    if (
      decodedResponse.status === ErrorCodes.SECTION_MAINTENANCE &&
      decodedResponse.error.code === ErrorMessages.PERM_DEACTIVATED
    ) {
      this.alertService.showError(
        this.translateService.translate('COMMON.DISPLAY_NOT_AVAILABLE'),
        this.translateService.translate('MAINTENANCE.SECTION.ERROR')
      );
    } else {
      let alertMsg = '';

      if (isPtUser) {
        alertMsg = decodedResponse.error.message;
      } else {
        alertMsg = errorMsg ? this.translateService.translate(errorMsg) : '';
      }

      this.alertService.showError(this.translateService.translate('COMMON.DISPLAY_NOT_AVAILABLE'), alertMsg);
    }
  }

  private openExternal(url: string) {
    this.loadingService.hide();
    window.open(url, '_blank');
  }

  open(response: MSafeAny) {
    try {
      this.logger.debug('blob is:', response);
      const blob: Blob = new Blob([response], { type: 'application/pdf' });

      this.logger.debug('blob is:', blob);
      const fileurl: string = window.URL.createObjectURL(blob);
      this.logger.debug('fileurl is:', fileurl);

      this.openExternal(fileurl);
    } catch (err) {
      this.loadingService.hide();
      this.logger.error(err);
      throw err;
    }
  }
}
